/* eslint-disable no-use-before-define */
import seatmapParser from './seatmap-parser';
// We import three.js ONLY for math operations, like matrix multiplications, etc.
// WE DO NOT USE IT FOR RENDERING AS THIS IS A MANAGER
import Base64Helper from '../../helpers/base64-helper';
//import {Buffer as BinaryBuffer} from 'buffer';
import pako from 'pako';
import {
    VIEW_MODE_MANAGE_DASHBOARD,
    VIEW_MODE_PAS,
} from '../../constants/global-constants';
import boundingBoxHelperFactory from '../../helpers/bounding-box-helper';
import geometryHelper from '../../helpers/geometry-helper';
import responsiveHelper from '../../helpers/responsive-helper';
import selectionHelper from '../../helpers/selection-helper';
import { venueStencilHelperFactory } from '../../helpers/venue-stencil-helper';

import {
    AUTOZOOM_VIEW_SIZE,
    BOUNDING_BOX_EMPTY,
    CHAIR_RADIUS_IN_UNITS,
    DEFAULT_VIEWBOX,
    MAX_ALLOWED_DISTANCE_FOR_TOUCH,
    MAX_BOX_FACTOR,
    MAX_CHAIR_RADIUS_IN_PIXELS,
    MAX_ITER_ADJUST,
    MIN_ANIMATION_DISTANCE,
    MIN_CHAIR_RADIUS_IN_PIXELS,
    VIEWBOX_ANIMATION_TIME,
    WAIT_TIME_CONFIRM_TOUCH,
    ZOOM_BUTTON_FACTOR,
    ZOOM_FACTOR,
} from './seatmap-constants';

import {
    APP_TYPE_BROWSER,
    BOUNDING_BOX_MARGIN_PAS,
    LOOKAT_MARGIN,
    SCREEN_CENTER_TARGET_X,
    SCREEN_CENTER_TARGET_Y,
} from '../../constants/global-constants';

const { base64ToArrayBuffer, utf8ArrayToStr } = Base64Helper;

const {
    addMarginToBoundingBox,
    applyMatrixToPoint,
    cartesianToParametric,
    cloneBoundingBox,
    getCenterFromBoundingBox,
    getDistance,
    getMatrixFromProperties,
    parametricToCartesian,
} = geometryHelper;

const { getTicketPositionNormalizedCoordinates, isNarrowScreen } =
    responsiveHelper;

const { getOverlappingObjects } = selectionHelper;

const seatmapManagerFactory = ({
    aGetAsset,
    getAppType,
    getSeatInfoFromIds,
    getUpstreamInteractive,
    getViewMode,
    isSeatLocked,
    model,
    pickSeats,
}) => {
    const _boundingBoxHelper = boundingBoxHelperFactory();
    const _venueStencilHelper = venueStencilHelperFactory({ aGetAsset });
    const { aGrabStencils, parseStencils } = _venueStencilHelper;

    const setPanning = (value) => {
        model.set('interactive.panning', value);
    };

    const _getPositionFromId = ({ id }) => {
        const seatInfo = getSeatInfoFromIds({ ids: [id] });
        const { rowId, sectionId, unitId } = seatInfo;

        const sectionProperties = model.get(
            `objects.contents.${sectionId}.properties`,
        );

        if (!sectionProperties) {
            return null;
        }
        const matrix = getMatrixFromProperties({
            properties: sectionProperties,
        });

        if (unitId) {
            let chairsPath;

            if (rowId) {
                chairsPath = `objects.contents.${sectionId}.contents.${sectionId}-${rowId}.chairs`;
            } else {
                chairsPath = `objects.contents.${sectionId}.chairs`;
            }
            const chairs = model.get(chairsPath);

            if (chairs) {
                const index = +unitId - 1;

                // eslint-disable-next-line no-restricted-globals
                if (!isNaN(index)) {
                    const x = chairs.x[index];
                    const y = chairs.y[index];
                    const pos = geometryHelper.applyMatrixToPoint({
                        matrix,
                        p: { x, y, z: 0 },
                    });

                    return pos;
                }
            }
        } else {
            let propertiesPath;

            if (rowId) {
                propertiesPath = `objects.contents.${sectionId}.contents.${sectionId}-${rowId}.properties`;
            } else {
                propertiesPath = `objects.contents.${sectionId}.properties`;
            }
            const properties = model.get(propertiesPath);

            if (properties) {
                const x = properties.sx || 0;
                const y = properties.sy || 0;
                const pos = applyMatrixToPoint({ matrix, p: { x, y, z: 0 } });

                return pos;
            }
        }
        return null;
    };

    const getPositionFromIds = ({ ids = [] }) => {
        if (!ids.length) {
            return null;
        }
        const totalPos = { x: 0, y: 0 };
        let validPositions = 0;

        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const position = _getPositionFromId({ id });

            if (position) {
                totalPos.x += position.x;
                totalPos.y += position.y;
                validPositions++;
            }
        }
        if (!validPositions) {
            return null;
        }
        return {
            x: totalPos.x / validPositions,
            y: totalPos.y / validPositions,
        };
    };

    const getViewport = () => {
        const viewport = model.get('interactive.viewport');

        return viewport;
    };

    const getBoundingBox = () => {
        const node = model.get();
        const boundingBox = _boundingBoxHelper.getBoundingBox({ node });

        return boundingBox;
    };

    const getZoomToFitViewbox = () => {
        let viewBox = getBoundingBox();

        if (viewBox) {
            const appType = getAppType();

            if (appType === APP_TYPE_BROWSER) {
                viewBox = addMarginToBoundingBox({
                    boundingBox: viewBox,
                    margin: BOUNDING_BOX_MARGIN_PAS,
                });
            }
        }
        return viewBox;
    };

    const getViewBox = () => {
        let viewBox = model.get('interactive.viewBox');

        if (!viewBox) {
            viewBox = getZoomToFitViewbox();
        }
        if (viewBox) {
            return cloneBoundingBox(viewBox);
        }
        viewBox = cloneBoundingBox(DEFAULT_VIEWBOX);
        return viewBox;
    };

    const getTicketSelectionInfo = () => {
        let visible = model.get('interactive.ticketSelection.visible');
        const useArrow = model.get('interactive.ticketSelection.useArrow');

        const ids = model.get('interactive.ticketSelection.ids');
        const position = model.get('interactive.ticketSelection.position');
        const animating = model.get('interactive.animatingViewBox');

        if (animating) {
            visible = false;
        }
        return {
            ids,
            position,
            useArrow,
            visible,
        };
    };

    const getWorldToPixelsFactor = ({ viewBox, viewport }) => {
        const width = viewBox.max.x - viewBox.min.x;
        const height = viewBox.max.y - viewBox.min.y;
        const wFactor = viewport.width / width;
        const hFactor = viewport.height / height;
        const factor = Math.min(wFactor, hFactor);

        return factor;
    };

    const getChairRadiusInPixels = ({ viewBox, viewport }) => {
        const factor = getWorldToPixelsFactor({ viewBox, viewport });
        const chairRadiusInPixels = CHAIR_RADIUS_IN_UNITS * factor;

        return chairRadiusInPixels;
    };

    const _computeCenter = (box = BOUNDING_BOX_EMPTY) => ({
        x: (box.max.x + box.min.x) * 0.5,
        y: (box.max.y + box.min.y) * 0.5,
    });

    const _computeSize = (box = BOUNDING_BOX_EMPTY) => ({
        x: box.max.x - box.min.x,
        y: box.max.y - box.min.y,
    });

    // Returns an adjusted viewbox to the actual viewport.
    // Please take into account that the viewBox is the desired area to view
    // and it can have a different aspect ratio as the viewport. We need
    // to adjust one to the other so that we can make calculations on
    // final rendering positions
    const getAdjustedViewBox = (viewBox) => {
        const viewport = getViewport();
        const viewSize = _computeSize(viewBox);
        const viewCenter = _computeCenter(viewBox);

        const viewportFactor = viewport.width / viewport.height;
        const viewBoxFactor = viewSize.x / viewSize.y;

        if (viewBoxFactor > viewportFactor) {
            // Our viewBox width will match our viewport width
            // but it will be shorter
            viewSize.y = (viewSize.x * viewport.height) / viewport.width;
        } else {
            // our viewBox height will match our viewport height
            // bit it will be narrower
            viewSize.x = (viewport.width * viewSize.y) / viewport.height;
        }

        // We calculate an adjusted viewBox to the viewport
        const adjustedViewBox = {
            max: {
                x: viewCenter.x + viewSize.x * 0.5,
                y: viewCenter.y + viewSize.y * 0.5,
            },
            min: {
                x: viewCenter.x - viewSize.x * 0.5,
                y: viewCenter.y - viewSize.y * 0.5,
            },
        };

        return adjustedViewBox;
    };

    const _validViewBox = (viewBox) => {
        const boundingBox = getBoundingBox();
        const viewport = getViewport();

        if (!viewport) {
            return true;
        }

        const chairRadiusInPixels = getChairRadiusInPixels({
            viewBox,
            viewport,
        });

        if (chairRadiusInPixels > MAX_CHAIR_RADIUS_IN_PIXELS) {
            return false;
        }

        const adjustedViewBox = getAdjustedViewBox(viewBox);

        const adjustedViewWidth = adjustedViewBox.max.x - adjustedViewBox.min.x;
        const adjustedViewHeight =
            adjustedViewBox.max.y - adjustedViewBox.min.y;

        const bboxSize = _computeSize(boundingBox);
        // Checking too much zoom in

        if (
            adjustedViewWidth > bboxSize.x * MAX_BOX_FACTOR &&
            adjustedViewHeight > bboxSize.y * MAX_BOX_FACTOR
        ) {
            return false;
        }

        const minAdjusted = adjustedViewBox.min;
        const maxAdjusted = adjustedViewBox.max;
        const minBBox = boundingBox.min;
        const maxBBox = boundingBox.max;

        if (
            minAdjusted.x > maxBBox.x ||
            minAdjusted.y > maxBBox.y ||
            maxAdjusted.x < minBBox.x ||
            maxAdjusted.y < minBBox.y
        ) {
            return false;
        }

        return true;
    };

    const aShowTicketSelection = ({ ids, visible }) => {
        model.set('interactive.ticketSelection.visible', visible);
        model.set('interactive.ticketSelection.ids', ids);

        if (!visible) {
            const hoverObject = model.get('globals.hoverObject');

            if (hoverObject) {
                model.set('globals.hoverObject', null);
            }
            const hoverId = model.get('globals.hoverId');

            if (hoverId) {
                model.set('globals.hoverId', null);
            }
            return Promise.resolve();
        }

        setPanning(false);

        const viewport = getViewport();
        const narrow = isNarrowScreen({ viewport });
        const viewMode = getViewMode();
        // If the screen is narrow, we will use arrow positioning instead of
        // corner positioning
        model.set('interactive.ticketSelection.useArrow', narrow);
        selectSeats(ids);
        if (narrow || viewMode === VIEW_MODE_PAS) {
            const normalizedPos = getTicketPositionNormalizedCoordinates({
                viewport,
            });

            return aLookAt({
                ids,
                targetPositionInNormalizedViewCoordinates: normalizedPos,
            }).then(() => {
                model.set('interactive.ticketSelection.visible', visible);
                model.set(
                    'interactive.ticketSelection.position',
                    normalizedPos,
                );
            });
        }

        if (ids && ids.length) {
            const positionInWorldCoordinates = getPositionFromIds({ ids });
            const positionNormalized = worldToNormalized(
                positionInWorldCoordinates,
            );

            model.set(
                'interactive.ticketSelection.position',
                positionNormalized,
            );
        }
        return Promise.resolve();
    };

    const setViewBox = (box) => {
        if (box) {
            const animating = model.get('interactive.animatingViewBox');

            if (!animating) {
                const ticketSelectionInfo = getTicketSelectionInfo();

                if (ticketSelectionInfo && ticketSelectionInfo.visible) {
                    return;
                }
                aShowTicketSelection({ visible: false });
            }
            if (_validViewBox(box)) {
                const boxToSet = cloneBoundingBox(box);

                model.set('interactive.viewBox', boxToSet);
            } else {
                const lastBox = getViewBox();

                if (!_validViewBox(lastBox)) {
                    const boxToSet = cloneBoundingBox(box);

                    model.set('interactive.viewBox', boxToSet);
                } else {
                    const adjustedBox = cloneBoundingBox(
                        _adjustBoxToValid(lastBox, box),
                    );

                    model.set('interactive.viewBox', adjustedBox);
                }
            }
        }
    };

    /*
    HOW ANIMATION WORKS.

    In order to avoid a continuous render loop when there is nothing to render, we must know if some part of the application is
    animating. To avoid race conditions on a single value for animation, we have a set of "animation contexts". Each subpart of
    the application can set or reset its own animation context, working independently from other animation contexts.

    We will return "true" to isAnimating when there is at least one animation context.


    REPAINTING

    To see how repainting is decoupled, please have a look at the three-viewer component.

    */
    const getIsAnimating = () => {
        const selSeats = getSelectedSeats();

        // For now we will only be animating without any change in the model
        // if we have seats selected as they blink. In the future, we may want to
        // have further logic
        if (selSeats.length > 0) {
            return true;
        }
        return false;
    };

    const animateViewBox = ({ end, time: totalTime }) =>
        new Promise((resolve) => {
            const startTime = Date.now();
            const startBox = getViewBox();

            const startCenter = getCenterFromBoundingBox(startBox);
            const endCenter = getCenterFromBoundingBox(end);
            const distance = getDistance({ p1: startCenter, p2: endCenter });

            if (distance < MIN_ANIMATION_DISTANCE) {
                model.set('interactive.animatingViewBox', false);
                setViewBox(end);
                resolve();
            } else {
                model.set('interactive.animatingViewBox', true);
                const _animate = () => {
                    const curTime = Date.now();
                    const deltaTime = curTime - startTime;

                    let u = deltaTime / totalTime;

                    if (u > 1) {
                        u = 1;
                    }

                    u = (1 - Math.cos(u * Math.PI)) / 2;

                    const newBox = {
                        max: {
                            x: startBox.max.x * (1 - u) + end.max.x * u,
                            y: startBox.max.y * (1 - u) + end.max.y * u,
                        },
                        min: {
                            x: startBox.min.x * (1 - u) + end.min.x * u,
                            y: startBox.min.y * (1 - u) + end.min.y * u,
                        },
                    };

                    setViewBox(newBox);
                    if (u < 1) {
                        requestAnimationFrame(_animate);
                    } else {
                        model.set('interactive.animatingViewBox', false);
                        resolve();
                    }
                };

                requestAnimationFrame(_animate);
            }
        });

    const aLookAt = ({
        ids,
        margin,
        targetPositionInNormalizedViewCoordinates,
        targetViewBox,
    }) =>
        new Promise((resolve) => {
            const viewBox = getViewBox();

            let boxWidth = viewBox.max.x - viewBox.min.x;

            let boxHeight = viewBox.max.y - viewBox.min.y;

            let position;

            if (targetViewBox) {
                position = getCenterFromBoundingBox(targetViewBox);
                let targetWidth = targetViewBox.max.x - targetViewBox.min.x;

                let targetHeight = targetViewBox.max.y - targetViewBox.min.y;

                targetWidth *= 1 + margin;
                targetHeight *= 1 + margin;

                boxWidth = Math.max(targetWidth, AUTOZOOM_VIEW_SIZE);
                boxHeight = Math.max(targetHeight, AUTOZOOM_VIEW_SIZE);
            }
            const lookAtPos = position || getPositionFromIds({ ids });

            const { x: leftRatio = 0.5, y: topRatio = 0.5 } =
                targetPositionInNormalizedViewCoordinates || {};

            if (lookAtPos) {
                // We have to adjust here using viewportSize for landscape and portrait
                let bh = boxHeight;

                let bw = boxWidth;
                const viewport = getViewport();

                if (viewport) {
                    const vw = viewport.width;
                    const vh = viewport.height;
                    const vf = viewport.width / viewport.height;
                    const bf = boxWidth / boxHeight;

                    // If the viewBox is wider than the viewport

                    if (bf > vf) {
                        bh = (bw * vh) / vw;
                    } else {
                        bw = (bh * vw) / vh;
                    }
                }

                const newViewBox = {
                    max: {
                        x: lookAtPos.x + bw * (1 - leftRatio),
                        y: lookAtPos.y + bh * (1 - topRatio),
                    },
                    min: {
                        x: lookAtPos.x - bw * leftRatio,
                        y: lookAtPos.y - bh * topRatio,
                    },
                };

                animateViewBox({
                    end: newViewBox,
                    start: viewBox,
                    time: VIEWBOX_ANIMATION_TIME,
                }).then(() => {
                    setTargetPositionInNormalizedCoordiantes({
                        x: leftRatio,
                        y: topRatio,
                    });
                    resolve();
                });
            } else {
                console.error('Do not know where to look at');
                resolve();
            }
        });

    const worldToNormalized = (pos) => {
        // We get the "target viewbox"
        const viewBox = getViewBox();
        // We get the "adjusted viewbox" taking into account viewport aspect ratio
        const adjustedViewBox = getAdjustedViewBox(viewBox);
        const adjustedWidth = adjustedViewBox.max.x - adjustedViewBox.min.x;
        const adjustedHeight = adjustedViewBox.max.y - adjustedViewBox.min.y;
        const x = (pos.x - adjustedViewBox.min.x) / adjustedWidth;
        const y = (pos.y - adjustedViewBox.min.y) / adjustedHeight;
        // Note
        // 0<=x<=1
        // 0<=y<=1

        return { x, y };
    };

    const parseSeatmapAndHolds = async ({ seatmap, mapStatus }) => {
        if (!seatmap) {
            return null;
        }

        const t1 = Date.now();

        const data = base64ToArrayBuffer(seatmap.data);

        const t2 = Date.now();
        const unzippedData = pako.inflate(data);
        const t2a = Date.now();
        const dataStr = utf8ArrayToStr(unzippedData);
        const t3 = Date.now();
        const venuemap = JSON.parse(dataStr);
        const t4 = Date.now();

        console.log(`Creating buffer from base64 data took ${t2 - t1}`);
        console.log(`Unzipping data took ${t2a - t2}`);
        console.log(`Uint8 data to string took ${t3 - t2a}`);
        console.log(`Parsing venue map took ${t4 - t3}`);
        console.log(`Total map parse time ${t4 - t1}`);
        const parsedSeatmap = seatmapParser.parseSeatmap({
            seatmap: { venuemap },
        });
        const objects = parsedSeatmap.objects;
        const holds = seatmapParser.parseHoldsFromGroups({
            groups: seatmap.groups,
            status: mapStatus,
        });
        const venueStencils = parseStencils(objects, {});

        const stencilData = await aGrabStencils(venueStencils);

        if (parsedSeatmap && stencilData) {
            parsedSeatmap['venue_stencils'] = stencilData;
        }
        return {
            seatmapModel: parsedSeatmap,
            holds,
        };
    };

    /*
        Parses the tiers info. The info has two possible sources, depending on
        the view mode. When in manage mode the info is returned from the inventory
        tiers endpoint. In any other mode the info comes from the tier_colors spec.
        The inventory tiers data returned by the inventory tiers endpoint is an array
        while the data in tier_colors is an object so we need to parse them differently.
    */
    const parseTiers = ({ tiersInfo = [], tierColors = {} }) => {
        let tiersModel = {};

        if (tiersInfo.length) {
            tiersModel = seatmapParser.parseTiersInfo({
                tiersInfo,
            });
        } else {
            tiersModel = seatmapParser.parseTierColors({
                tierColors,
            });
        }

        return tiersModel;
    };

    const parseStatus = ({ holdStatus, mapStatus }) => {
        const statusModel = seatmapParser.parseStatus({
            holdStatus,
            mapStatus,
        });

        return statusModel;
    };

    const parseHoldsFromGroups = ({ groups }) => {
        const status = model.get('status');
        const result = seatmapParser.parseHoldsFromGroups({ groups, status });

        return result;
    };

    // Temporary solution while we do not have all maps having the tiers
    // in the venue map structure and we have them in the status
    const mergeTiersFromStatusToMap = ({ seatmapModel, statusModel }) => {
        const _addTiers = (iter, statusModel) => {
            if (!iter || !statusModel) {
                return;
            }
            if (iter.properties) {
                const id = iter.properties.id;

                if (statusModel[id]) {
                    if (statusModel[id].t) {
                        if (iter.chairs) {
                            // eslint-disable-next-line no-param-reassign
                            iter.chairs.t = statusModel[id].t;
                        }
                    } else if (statusModel[id].T) {
                        // eslint-disable-next-line no-param-reassign
                        iter.properties.t = statusModel[id].T;
                    }
                }
            }
            if (iter.contents) {
                Object.keys(iter.contents).forEach((key) => {
                    const child = iter.contents[key];

                    _addTiers(child, statusModel);
                });
            }
        };

        _addTiers(seatmapModel.objects, statusModel);
        // Now we will remove tiers from the status
        Object.keys(statusModel).forEach((key) => {
            const record = statusModel[key];

            if (record.t) {
                delete record.t;
            }
            if (record.T) {
                delete record.T;
            }
        });
    };

    const setViewport = (viewport) => {
        const { height, width } = viewport || {};

        model.set('interactive.viewport', { height, width });
    };

    const _interpolateBox = (goodBox, badBox, u) => ({
        max: {
            x: (1 - u) * goodBox.max.x + u * badBox.max.x,
            y: (1 - u) * goodBox.max.y + u * badBox.max.y,
        },
        min: {
            x: (1 - u) * goodBox.min.x + u * badBox.min.x,
            y: (1 - u) * goodBox.min.y + u * badBox.min.y,
        },
    });

    const _adjustBoxToValid = (goodBox, badBox) => {
        let uMax = 1;

        let uMin = 0;

        for (let i = 0; i < MAX_ITER_ADJUST; i++) {
            const u = (uMax + uMin) * 0.5;
            const interpolatedBox = _interpolateBox(goodBox, badBox, u);

            if (_validViewBox(interpolatedBox)) {
                uMin = u;
            } else {
                uMax = u;
            }
        }
        const finalBox = _interpolateBox(goodBox, badBox, uMin);

        return finalBox;
    };

    const _getIdFromIntersects = (intersects) => {
        if (intersects && intersects.length) {
            const intersect = intersects[0];
            const index = intersect.index;
            const referrer = intersect.userData.referrer;
            const ids = referrer.ids;
            const id = ids[index];

            return id || '';
        }

        return '';
    };

    // Returns true if seats are small for picking in touch devices
    // or to have a good selection for manage dashboard
    const seatsAreSmall = ({ viewBox, viewport }) => {
        const chairRadiusInPixels = getChairRadiusInPixels({
            viewBox,
            viewport,
        });

        if (chairRadiusInPixels < MIN_CHAIR_RADIUS_IN_PIXELS) {
            return true;
        }

        return false;
    };

    const canPickASeat = ({ id, isTouch, viewBox }) => {
        if (!isTouch) {
            return true;
        }
        // If it is a GA seat, I can pick it always
        if (id && id.indexOf(':') === -1) {
            return true;
        }
        const viewport = getViewport();

        if (!viewport) {
            return false;
        }
        if (seatsAreSmall({ viewBox, viewport })) {
            return false;
        }
        return true;
    };

    const isPanning = () => {
        const value = model.get('interactive.panning');

        return value;
    };

    const isDefiningGrid = () => {
        const value = model.get('interactive.grid.defining');

        return value;
    };

    const getSelectionGrid = () => {
        if (!isDefiningGrid()) {
            return null;
        }
        const grid = model.get('interactive.grid');

        return grid;
    };

    const setTargetPositionInNormalizedCoordiantes = (pos) => {
        model.set('interactive.targetPositionInNormalizedCoordinates', pos);

        return pos;
    };

    const getTargetPositionInNormalizedCoordinates = () => {
        const pos = model.get(
            'interactive.targetPositionInNormalizedCoordinates',
        );

        return pos;
    };

    const getTierFromId = ({ id }) => {
        const seatInfo = getSeatInfoFromIds({ ids: [id] });
        const { rowId, sectionId, unitId } = seatInfo;

        if (unitId) {
            let chairsPath;

            if (rowId) {
                chairsPath = `objects.contents.${sectionId}.contents.${sectionId}-${rowId}.chairs`;
            } else {
                chairsPath = `objects.contents.${sectionId}.chairs`;
            }
            const chairs = model.get(chairsPath);

            if (chairs) {
                const index = +unitId - 1;

                // eslint-disable-next-line no-restricted-globals
                if (!isNaN(index)) {
                    const tier = chairs.t[index];

                    return tier;
                }
            }
        } else {
            let propertiesPath;

            if (rowId) {
                propertiesPath = `objects.contents.${sectionId}.contents.${sectionId}-${rowId}.properties`;
            } else {
                propertiesPath = `objects.contents.${sectionId}.properties`;
            }
            const properties = model.get(propertiesPath);

            if (properties) {
                const tier = properties.t;

                return tier;
            }
        }
        return 0;
    };

    const getTiersFromSectionId = (sectionId) => {
        const tiersIdsObj = {};
        const gaSectionTier = model.get(
            `objects.contents.${sectionId}.properties.t`,
        );
        const seatingSectionContents = model.get(
            `objects.contents.${sectionId}.contents`,
        );
        const tableAndChairsTiers = model.get(
            `objects.contents.${sectionId}.chairs.t`,
        );

        // GA Section
        if (gaSectionTier) {
            tiersIdsObj[gaSectionTier] = true;
            // Reserved Seating section
        } else if (seatingSectionContents) {
            Object.keys(seatingSectionContents).forEach((contentKey) => {
                const chairsTiers =
                    model.get(
                        `objects.contents.${sectionId}.contents.${contentKey}.chairs.t`,
                    ) || [];

                chairsTiers.forEach(
                    (chairTier) => (tiersIdsObj[chairTier] = true),
                );
            });
            // Reserved table and chairs
        } else if (tableAndChairsTiers) {
            tableAndChairsTiers.forEach(
                (chairTier) => (tiersIdsObj[chairTier] = true),
            );
        }

        return Object.keys(tiersIdsObj);
    };

    const aZoomToObject = async (id) => {
        const obj = model.get(`objects.contents.${id}`);

        if (!obj) {
            return;
        }
        const targetViewBox =
            _boundingBoxHelper.computeBoundingBoxRecursive(obj);

        await aLookAt({
            targetViewBox,
            margin: LOOKAT_MARGIN,
            targetPositionInNormalizedViewCoordinates: {
                x: SCREEN_CENTER_TARGET_X,
                y: SCREEN_CENTER_TARGET_Y,
            },
            autoZoom: true,
        });
    };

    const tryPickSeats = ({ ids = [], isTouch, pos }) => {
        const id = ids[0];
        const isLocked = isSeatLocked({ id });

        const viewBox = getViewBox();
        const canPick = canPickASeat({
            id,
            isTouch,
            viewBox,
        });

        if (!canPick) {
            aZoomToObject({ pos });
        } else if (!isLocked) {
            setPanning(false);

            const selectedSeats = ids.map((id) => ({ id }));

            pickSeats({ selectedSeats });
        }
    };

    const selectSeats = (seats) => {
        model.set('globals.selected.seats', seats);
    };

    const getSelectedSeats = () => model.get('globals.selected.seats') || [];

    const setDefiningGrid = (value) => {
        model.set('interactive.grid.defining', value);
        if (!value) {
            model.set('interactive.grid.startPos', null);
            model.set('interactive.grid.curPos', null);
        }
    };

    const seatmapMouseDown = ({ intersects, isTouch, keys, pos }) => {
        const viewMode = getViewMode();
        const id = _getIdFromIntersects(intersects);

        const upstreamInteractive = getUpstreamInteractive();

        if (upstreamInteractive) {
            const handled = upstreamInteractive.mouseDown({
                keys,
                pos,
                unitId: id,
            });

            if (handled) {
                return;
            }
        }

        const viewBox = getViewBox();

        const { visible } = getTicketSelectionInfo();

        if (visible) {
            aShowTicketSelection({ visible: false });
        }

        model.set('interactive.startMousePos', pos);

        // We only do grid selection if we are not in pick-a-seat mode
        if (keys.shift && viewMode !== VIEW_MODE_PAS) {
            setDefiningGrid(true);
            model.set('interactive.grid.startPos', pos);
            model.set('interactive.grid.curPos', null);
        } else {
            // We define panning to be true, so that on touch we
            // do panning by default. We will cancel panning after X milliseconds
            // in the code below
            setPanning(true);

            model.set('interactive.startPanBox', viewBox);

            const _doMouseDownAction = () => {
                selectSeats([]);
                switch (viewMode) {
                    case VIEW_MODE_MANAGE_DASHBOARD:
                    case VIEW_MODE_PAS:
                        if (id) {
                            tryPickSeats({
                                ids: [id],
                                isTouch,
                                pos,
                            });
                        }
                        break;
                    default:
                        break;
                }
            };

            const startViewBox = getViewBox();

            setTimeout(() => {
                // If we are touching, we have to check that after
                // wait_time_confirm_touch milliseconds no movement
                // took place before triggering a pick a seat event

                const currentViewBox = getViewBox();

                const dMin = getDistance({
                    p1: startViewBox.min,
                    p2: currentViewBox.min,
                });
                const dMax = getDistance({
                    p1: startViewBox.max,
                    p2: currentViewBox.max,
                });

                if (
                    dMin < MAX_ALLOWED_DISTANCE_FOR_TOUCH &&
                    dMax < MAX_ALLOWED_DISTANCE_FOR_TOUCH
                ) {
                    _doMouseDownAction();
                }
            }, WAIT_TIME_CONFIRM_TOUCH);
        }
    };

    const selectObjects = ({ endPos, startPos }) => {
        const objects = model.get('objects');
        const startPosToUse = {
            x: Math.min(startPos.x, endPos.x),
            y: Math.min(startPos.y, endPos.y),
        };
        const endPosToUse = {
            x: Math.max(startPos.x, endPos.x),
            y: Math.max(startPos.y, endPos.y),
        };
        const overlappingObjects = getOverlappingObjects(
            startPosToUse,
            endPosToUse,
            objects,
            null,
        );

        selectSeats(overlappingObjects);
    };

    const seatmapMouseUp = () => {
        const upstreamInteractive = getUpstreamInteractive();

        if (isDefiningGrid()) {
            const startPos = model.get('interactive.grid.startPos');
            const endPos = model.get('interactive.grid.curPos');

            if (startPos && endPos) {
                selectObjects({ endPos, startPos });
                const viewMode = getViewMode();

                if (viewMode === VIEW_MODE_MANAGE_DASHBOARD) {
                    const selectedSeats = getSelectedSeats();

                    if (selectedSeats.length) {
                        pickSeats({
                            selectedSeats: selectedSeats.map((id) => ({ id })),
                        });
                    }
                }
            }
        } else if (upstreamInteractive) {
            upstreamInteractive.mouseUp();
        }

        setPanning(false);
        setDefiningGrid(false);
    };

    const seatmapWheel = ({ delta, pos }) => {
        const viewBox = getViewBox();

        const parametricCoords = cartesianToParametric({ box: viewBox, pos });
        const zoom = Math.pow(ZOOM_FACTOR, delta);

        viewBox.min.x *= zoom;
        viewBox.min.y *= zoom;
        viewBox.max.x *= zoom;
        viewBox.max.y *= zoom;

        const newPos = parametricToCartesian({
            box: viewBox,
            coords: parametricCoords,
        });
        const deltaBox = {
            x: pos.x - newPos.x,
            y: pos.y - newPos.y,
        };

        viewBox.min.x += deltaBox.x;
        viewBox.min.y += deltaBox.y;
        viewBox.max.x += deltaBox.x;
        viewBox.max.y += deltaBox.y;

        setViewBox(viewBox);
    };

    const seatmapMouseMove = ({ intersects, keys, pos }) => {
        const panning = isPanning();
        const definingGrid = isDefiningGrid();

        if (definingGrid) {
            model.set('interactive.grid.curPos', pos);
            return;
        }

        const viewMode = getViewMode();

        if (keys.shift && viewMode !== VIEW_MODE_PAS) {
            return;
        }

        if (panning) {
            const startMousePos = model.get('interactive.startMousePos');
            const startPanBox = model.get('interactive.startPanBox');

            const minPan = startPanBox.min;
            const maxPan = startPanBox.max;
            const deltaPos = {
                x: pos.x - startMousePos.x,
                y: pos.y - startMousePos.y,
                z: pos.z - startMousePos.z,
            };
            const newViewBox = {
                max: {
                    x: maxPan.x - deltaPos.x,
                    y: maxPan.y - deltaPos.y,
                },
                min: {
                    x: minPan.x - deltaPos.x,
                    y: minPan.y - deltaPos.y,
                },
            };

            setViewBox(newViewBox);
            return;
        }

        const upstreamInteractive = getUpstreamInteractive();

        if (upstreamInteractive) {
            upstreamInteractive.mouseMove({ pos });
        }

        const hoverId = _getIdFromIntersects(intersects) || '';
        const oldHoverId = model.get('globals.hoverId');

        if (hoverId !== oldHoverId) {
            model.set('globals.hoverId', hoverId);
            const sides = hoverId.split(':');
            const parts = sides[0].split('-');
            const hoverObject = parts[0];

            model.set('globals.hoverObject', hoverObject);
        }
    };

    const _zoom = (factor) => {
        const viewBox = getViewBox();

        const width = viewBox.max.x - viewBox.min.x;
        const height = viewBox.max.y - viewBox.min.y;
        const centerX = (viewBox.max.x + viewBox.min.x) * 0.5;
        const centerY = (viewBox.max.y + viewBox.min.y) * 0.5;

        aShowTicketSelection({ visible: false });
        setViewBox({
            max: {
                x: centerX + width * factor * 0.5,
                y: centerY + height * factor * 0.5,
            },
            min: {
                x: centerX - width * factor * 0.5,
                y: centerY - height * factor * 0.5,
            },
        });
    };

    const zoomIn = () => {
        _zoom(1 / ZOOM_BUTTON_FACTOR);
    };

    const zoomOut = () => {
        _zoom(ZOOM_BUTTON_FACTOR);
    };

    const zoomToFit = () => {
        const viewBox = getZoomToFitViewbox();

        setViewBox(viewBox);
    };

    return {
        aLookAt,
        aShowTicketSelection,
        aZoomToObject,
        getAdjustedViewBox,
        getChairRadiusInPixels,
        getIsAnimating,
        getPositionFromIds,
        getSelectedSeats,
        getSelectionGrid,
        getTargetPositionInNormalizedCoordinates,
        getTicketSelectionInfo,
        getTierFromId,
        getTiersFromSectionId,
        getViewBox,
        getViewport,
        getWorldToPixelsFactor,
        isDefiningGrid,
        isPanning,
        mergeTiersFromStatusToMap,
        parseHoldsFromGroups,
        parseSeatmapAndHolds,
        parseStatus,
        parseTiers,
        seatmapMouseDown,
        seatmapMouseMove,
        seatmapMouseUp,
        seatmapWheel,
        seatsAreSmall,
        selectSeats,
        setTargetPositionInNormalizedCoordiantes,
        setViewBox,
        setViewport,
        worldToNormalized,
        zoomIn,
        zoomOut,
        zoomToFit,
    };
};

export default seatmapManagerFactory;
